// Client wrapper for the Stripe billing endpoints on the api service (served
// same-origin at /api/billing/*). Mirrors lib/passkey.ts: thin POST helpers that
// forward the PocketBase session token so the server can resolve the caller.
// The endpoints only *kick* Stripe; the resulting plan/card/invoice state lands
// on the billing record via the webhook and reaches the UI over the store's
// realtime billing subscription.

import type { PlanId } from './types'
import { onAuthExpired } from './pb'

const BASE = '/api/billing'

async function postJson<T>(path: string, body: unknown, pbToken: string): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(pbToken ? { 'X-PB-Token': pbToken } : {}),
    },
    body: JSON.stringify(body ?? {}),
  })
  const data = await res.json().catch(() => ({}))
  if (res.status === 401) onAuthExpired()
  if (!res.ok) throw new Error((data as { error?: string })?.error || 'Billing request failed.')
  return data as T
}

/** Result of starting/switching a subscription. `requires_payment` means the
 *  client must confirm the returned clientSecret with Elements (first card);
 *  `updated` means an existing subscription was re-priced (no card step). */
export interface SubscribeResult {
  status: 'updated' | 'requires_payment'
  clientSecret?: string
}

/** Start or switch a paid plan for the org. */
export function subscribe(planId: PlanId, pbToken: string): Promise<SubscribeResult> {
  return postJson<SubscribeResult>('/subscribe', { planId }, pbToken)
}

/** Schedule the active subscription to downgrade to Free at period end. */
export function cancelSubscription(pbToken: string): Promise<{ ok: true }> {
  return postJson<{ ok: true }>('/cancel', {}, pbToken)
}

/** Undo a scheduled downgrade — keep the current plan. */
export function resumeSubscription(pbToken: string): Promise<{ ok: true }> {
  return postJson<{ ok: true }>('/resume', {}, pbToken)
}

/** Begin an "update card" flow — returns a SetupIntent client secret. */
export function createSetupIntent(pbToken: string): Promise<{ clientSecret: string }> {
  return postJson<{ clientSecret: string }>('/setup-intent', {}, pbToken)
}

/** Finish "update card" — adopt the freshly-saved card as the default. */
export function setDefaultPaymentMethod(
  paymentMethodId: string,
  pbToken: string,
): Promise<{ ok: true }> {
  return postJson<{ ok: true }>('/set-default-pm', { paymentMethodId }, pbToken)
}
